import type { EditorStore } from './editor-store';
import type { History } from './editor-history';
import type { SyncState } from '@/sync/sync-types';
import { validate } from './editor-validation';
import { haptic } from './editor-haptic';

export type PaletteTool =
  | null
  | 'spike' | 'wall' | 'star' | 'fragment' | 'powerup' | 'moving' | 'paste'
  | 'jumppad-small' | 'jumppad-medium' | 'jumppad-large'
  | 'orb-small' | 'orb-medium' | 'orb-large'
  | 'trigger:text' | 'trigger:color' | 'trigger:speed' | 'trigger:camera'
  | 'trigger:music' | 'trigger:gravity' | 'trigger:player_mode' | 'trigger:spawn';

/** 'none' = no sync button shown (signed out or sync not configured). */
export type ToolbarSyncState = SyncState | 'none';

export class EditorToolbar {
  private host: HTMLElement;
  private store: EditorStore;
  private history: History;
  private tool: PaletteTool = null;
  private onToolChange: (t: PaletteTool) => void;
  private syncState: ToolbarSyncState = 'none';

  onSaveRequest: (() => void) | null = null;
  onSaveBlockedRequest: (() => void) | null = null;
  onTestRequest: (() => void) | null = null;
  onExportRequest: (() => void) | null = null;
  onImportRequest: (() => void) | null = null;
  onColorReplaceRequest: (() => void) | null = null;
  onResetRequest: (() => void) | null = null;
  onSyncRequest: ((action: 'push' | 'pull' | 'resolve') => void) | null = null;

  constructor(host: HTMLElement, store: EditorStore, history: History, onToolChange: (t: PaletteTool) => void) {
    this.host = host;
    this.store = store;
    this.history = history;
    this.onToolChange = onToolChange;
    this.store.on('level-changed', () => this.render());
    this.render();
  }

  setTool(t: PaletteTool): void {
    this.tool = t;
    this.render();
    this.onToolChange(t);
  }

  setSyncState(state: ToolbarSyncState): void {
    this.syncState = state;
    this.render();
  }

  get currentTool(): PaletteTool { return this.tool; }

  private render(): void {
    // Preserve existing static buttons (Back, Outline toggle, Props toggle). Remove only our palette+tool buttons.
    this.host.querySelectorAll('.palette-btn, .tool-btn, .palette-spacer').forEach((n) => n.remove());

    const palette = [
      { id: 'spike' as const, label: '🔺 Spike' },
      { id: 'wall' as const, label: '🧱 Wall' },
      { id: 'moving' as const, label: '🔁 Moving' },
      { id: 'jumppad-small' as const,  label: '🔴 Pad S' },
      { id: 'jumppad-medium' as const, label: '🟢 Pad M' },
      { id: 'jumppad-large' as const,  label: '🔵 Pad L' },
      { id: 'orb-small' as const,      label: '🔴 Orb S' },
      { id: 'orb-medium' as const,     label: '🟢 Orb M' },
      { id: 'orb-large' as const,      label: '🔵 Orb L' },
      { id: 'star' as const, label: '⭐ Star' },
      { id: 'fragment' as const, label: '📿 Fragment' },
      { id: 'powerup' as const, label: '🧪 Power-up' },
      { id: 'paste' as const, label: '📋 Paste' },
      { id: 'trigger:text' as const, label: '💬 Text' },
      { id: 'trigger:color' as const, label: '🎨 Color' },
      { id: 'trigger:speed' as const, label: '💨 Speed' },
    ];
    for (const p of palette) {
      const btn = document.createElement('button');
      btn.className = 'palette-btn';
      btn.textContent = p.label;
      if (this.tool === p.id) btn.classList.add('active');
      btn.setAttribute('aria-pressed', this.tool === p.id ? 'true' : 'false');
      btn.setAttribute('aria-label', `Place ${p.id}`);
      btn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        this.setTool(this.tool === p.id ? null : p.id);
        haptic('tap');
      });
      this.host.appendChild(btn);
    }

    const spacer2 = document.createElement('div');
    spacer2.className = 'spacer palette-spacer';
    this.host.appendChild(spacer2);

    const undo = document.createElement('button');
    undo.className = 'tool-btn';
    undo.textContent = '↶ Undo';
    undo.disabled = !this.history.canUndo();
    undo.setAttribute('aria-label', 'Undo');
    if (undo.disabled) undo.setAttribute('aria-disabled', 'true');
    undo.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.history.undo();
      this.store.notify('level-changed');
      this.render();
    });
    this.host.appendChild(undo);

    const redo = document.createElement('button');
    redo.className = 'tool-btn';
    redo.textContent = '↷ Redo';
    redo.disabled = !this.history.canRedo();
    redo.setAttribute('aria-label', 'Redo');
    if (redo.disabled) redo.setAttribute('aria-disabled', 'true');
    redo.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.history.redo();
      this.store.notify('level-changed');
      this.render();
    });
    this.host.appendChild(redo);

    const test = document.createElement('button');
    test.className = 'tool-btn primary';
    test.textContent = '▶ Test';
    test.setAttribute('aria-label', 'Play-test level');
    test.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.onTestRequest?.(); });
    this.host.appendChild(test);

    const save = document.createElement('button');
    save.className = 'tool-btn';
    save.textContent = '💾 Save';
    save.setAttribute('aria-label', 'Save level');
    const hasBlockers = validate(this.store.level).blocking.length > 0;
    if (hasBlockers) {
      save.classList.add('blocked');
      save.title = 'Fix issues in outline first';
      save.setAttribute('aria-disabled', 'true');
    } else {
      save.classList.remove('blocked');
      save.title = '';
      save.removeAttribute('aria-disabled');
    }
    save.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (hasBlockers) {
        this.onSaveBlockedRequest?.();
        return;
      }
      this.onSaveRequest?.();
    });
    this.host.appendChild(save);

    const exp = document.createElement('button');
    exp.className = 'tool-btn';
    exp.textContent = '⬆ Export';
    exp.setAttribute('aria-label', 'Export level');
    exp.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.onExportRequest?.(); });
    this.host.appendChild(exp);

    const imp = document.createElement('button');
    imp.className = 'tool-btn';
    imp.textContent = '⬇ Import';
    imp.setAttribute('aria-label', 'Import level or pack');
    imp.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.onImportRequest?.(); });
    this.host.appendChild(imp);

    const fr = document.createElement('button');
    fr.className = 'tool-btn';
    fr.textContent = '🎨 Replace';
    fr.setAttribute('aria-label', 'Find and replace color');
    fr.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.onColorReplaceRequest?.(); });
    this.host.appendChild(fr);

    const lvl = this.store.level;
    if (lvl.meta.source?.kind === 'builtin') {
      const reset = document.createElement('button');
      reset.className = 'tool-btn';
      reset.textContent = '↻ Reset';
      reset.title = 'Reset this clone to the original builtin';
      reset.setAttribute('aria-label', 'Reset to original builtin');
      reset.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.onResetRequest?.(); });
      this.host.appendChild(reset);
    }

    const syncBtn = this.renderSyncButton();
    if (syncBtn) this.host.appendChild(syncBtn);
  }

  private renderSyncButton(): HTMLButtonElement | null {
    const state = this.syncState;
    if (state === 'none') return null;

    const btn = document.createElement('button');
    btn.className = 'tool-btn sync-btn';
    btn.setAttribute('aria-label', 'Sync level');

    type BtnConfig = { label: string; color: string; action: 'push' | 'pull' | 'resolve' | null };
    const cfg: BtnConfig = (() => {
      switch (state) {
        case 'local_only':  return { label: '⬆ Push',    color: '#00ffff', action: 'push' };
        case 'edited':      return { label: '⬆ Push',    color: '#ff8800', action: 'push' };
        case 'update_available': return { label: '⬇ Pull', color: '#8855ff', action: 'pull' };
        case 'conflict':    return { label: '⚡ Resolve', color: '#ff4444', action: 'resolve' };
        case 'synced':      return { label: '✓ Synced',  color: '#00cc66', action: null };
        case 'removed_from_server': return { label: '✗ Removed', color: '#888',   action: null };
      }
    })();

    btn.textContent = cfg.label;
    btn.style.cssText = `color:${cfg.color};border-color:${cfg.color}44;`;
    if (!cfg.action) {
      btn.style.opacity = '0.6';
      btn.style.cursor = 'default';
    }
    btn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (cfg.action) this.onSyncRequest?.(cfg.action);
    });
    return btn;
  }
}
